import React from 'react';
import { useTranslation } from 'react-i18next';

const cursedRocks = [
  {
    id: 1,
    normal: "https://i.ibb.co/QkfL0Zr/Screenshot-2024-11-26-223813.png",
    cursed: "https://i.ibb.co/y8dQk0H/Screenshot-2024-11-25-201656.png",
    name: "NodeRock #8559"
  },
  {
    id: 2,
    normal: "https://i.ibb.co/DfF73PH/Screenshot-2024-11-25-202226.png",
    cursed: "https://i.ibb.co/wBwxKwc/Screenshot-2024-11-25-202202.png",
    name: "NodeRock #4343"
  },
  {
    id: 3,
    normal: "https://i.ibb.co/0spHwSd/Screenshot-2024-11-25-202426.png",
    cursed: "https://i.ibb.co/WpP63QV/Screenshot-2024-11-25-202504.png",
    name: "NodeRock #672"
  },
  {
    id: 4,
    normal: "https://i.ibb.co/XsKBycn/Screenshot-2024-11-25-202714.png",
    cursed: "https://i.ibb.co/rFKfk48/Screenshot-2024-11-25-202740.png",
    name: "NodeRock #6104"
  }
];

export default function CursedRocks() {
  const { t } = useTranslation();

  return (
    <section className="relative bg-dark py-6 sm:py-12 md:py-20 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))]
          from-primary/10 via-transparent to-transparent animate-pulse"></div>
        
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,theme(colors.primary/0.5)_1px,transparent_1px),linear-gradient(-45deg,theme(colors.primary-light/0.5)_1px,transparent_1px)]"
               style={{ backgroundSize: '20px 20px', animation: 'glitch 2s infinite linear' }}></div>
        </div>

        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-primary/30 rounded-full animate-twinkle"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              filter: 'blur(1px)'
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        <div className="text-center mb-6 sm:mb-12 relative">
          <div className="inline-block">
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold relative">
              <span className="absolute -inset-2 text-primary opacity-50 animate-pulse blur-sm">
                {t('cursedRocks.title')}
              </span>
              <span className="relative text-transparent bg-clip-text bg-gradient-to-r 
                from-primary via-primary-light to-primary animate-gradient-x">
                {t('cursedRocks.title')}
              </span>
            </h2>
          </div>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mt-3 sm:mt-6">
            {t('cursedRocks.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-12">
          {cursedRocks.map((rock) => (
            <div key={rock.id} className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-primary-light 
                rounded-xl blur opacity-25 group-hover:opacity-100 transition-all duration-500"></div>
              <div className="relative card overflow-hidden">
                <div className="relative aspect-square transition-all duration-500 
                  group-hover:opacity-0 group-hover:scale-110">
                  <img 
                    src={rock.normal}
                    alt={`${rock.name} - ${t('cursedRocks.normal')}`}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary-light/20 
                    mix-blend-overlay"></div>
                </div>
                
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 
                  transition-all duration-500 transform scale-90 group-hover:scale-100">
                  <img 
                    src={rock.cursed}
                    alt={`${rock.name} - ${t('cursedRocks.cursed')}`}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-purple-500/20 
                    mix-blend-color-dodge animate-pulse"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-dark to-transparent"></div>
                </div>

                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-dark to-transparent">
                  <h3 className="text-lg font-bold text-white">{rock.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <span>{t('cursedRocks.hoverReveal')}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-3 sm:gap-6">
          <div className="card group hover:bg-dark-lighter/50">
            <h3 className="text-xl font-bold mb-4 text-primary">{t('cursedRocks.whatAre.title')}</h3>
            <p className="text-gray-300" dangerouslySetInnerHTML={{ 
              __html: t('cursedRocks.whatAre.description')
                .replace(/NodeRock/g, '<span class="text-primary">NodeRock</span>')
                .replace(/Cursed Rock/g, '<span class="text-primary">Cursed Rock</span>')
                .replace(/Yin Yang/g, '<span class="text-primary">Yin Yang</span>')
            }} />
          </div>
          <div className="card group hover:bg-dark-lighter/50">
            <h3 className="text-xl font-bold mb-4 text-primary">{t('cursedRocks.rarity.title')}</h3>
            <p className="text-gray-300" dangerouslySetInnerHTML={{ 
              __html: t('cursedRocks.rarity.description')
                .replace(/70,000/g, '<span class="text-primary">70,000</span>')
                .replace(/Buy 1, Get 2/g, '<span class="text-primary">Buy 1, Get 2</span>')
                .replace(/Yin Yang/g, '<span class="text-primary">Yin Yang</span>')
            }} />
          </div>
        </div>
      </div>
    </section>
  );
}